import { useCallback, useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router';
import { Paper } from '@/shared/ui';

import { FWDCURVE_CATALOG_PATH, SPOT_CATALOG_PATH } from '@/config/constants';
import { countRecentlyLaunched } from '@/entities/catalog-item';
import useDocumentTitleRename from '@/shared/hooks/useDocumentTitleRename';
import { PageInformationContext } from '@/shared/lib/PageInformationContext';

import { FactsheetDialogButton } from '@/features/factsheet';

import CatalogHero from '../components/CatalogHero/CatalogHero';
import CatalogSkeleton from '../components/CatalogSkeleton/CatalogSkeleton';
import CatalogTable from '../components/CatalogTable/CatalogTable';
import useCatalog from '../hooks/useCatalog';
import { FilterProvider } from '../state/filter.context';
import { useFilter } from '../state/useFilter';

const pageInformation = {
  mainPageTitle: 'CatalogTable'
};

const CatalogBase = () => {
  const location = useLocation();
  const pathname = location.pathname.toLowerCase();
  const isForwardCurves =
    pathname.includes(FWDCURVE_CATALOG_PATH) || pathname.includes('forward-curves');

  const {
    status,
    filterOpen,
    setFilterOpen,
    table,
    activeTab,
    expandedData,
    setExpandedData,
    showDiscontinued,
    setShowDiscontinued
  } = useCatalog(isForwardCurves);

  const unit = useMemo(() => {
    if (isForwardCurves) return 'forward curve';
    return expandedData ? 'item' : 'spot code';
  }, [expandedData, isForwardCurves]);

  useDocumentTitleRename('GX Go | Catalog');
  const { clearAllFilters } = useFilter();
  const navigate = useNavigate();

  const onTabChange = useCallback(() => {
    clearAllFilters();
    navigate(`/catalog/${isForwardCurves ? SPOT_CATALOG_PATH : FWDCURVE_CATALOG_PATH}`);
  }, [clearAllFilters, isForwardCurves, navigate]);

  if (status === 'loading') {
    return <CatalogSkeleton />;
  }

  const filteredRows = table.getFilteredRowModel().rows;
  const count = filteredRows.length;
  const newCount = countRecentlyLaunched(
    filteredRows.map(row => row.original),
    new Date()
  );

  return (
    <>
      <PageInformationContext value={pageInformation}>
        <CatalogHero
          activeTab={activeTab}
          count={count}
          newCount={newCount}
          unit={unit}
          onTabChange={onTabChange}
        />
      </PageInformationContext>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0
        }}
      >
        <CatalogTable
          expandedData={expandedData}
          filterOpen={filterOpen}
          isForwardCurves={isForwardCurves}
          renderFactsheetButton={(code, mode) => (
            <FactsheetDialogButton
              code={code}
              mode={mode}
            />
          )}
          setExpandedData={setExpandedData}
          setFilterOpen={setFilterOpen}
          setShowDiscontinued={setShowDiscontinued}
          showDiscontinued={showDiscontinued}
          table={table}
        />
      </Paper>
    </>
  );
};

export const CatalogPage = () => (
  <FilterProvider>
    <CatalogBase />
  </FilterProvider>
);
